import ApiClient from './ApiClient';

// Surfaces backend structured errors ({ error: "..." }, optionally { rowErrors: [...] } for CSV
// import) instead of the generic "Failed to X" wrapping PoolClient/GatewayClient use, since the
// colo admin screens (Plan 06 Task 3 / spec §5B.3) must render the API's actual validation text.
const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

// The colo/sources, colo/outlet-mappings and colo/collectors DTOs are NOT wire-pinned (unlike
// GET features/colo/org-features — see FeaturesResponseDto) and serialize as the exact C# record
// property names (PascalCase: Id, SourceType, ...). Wrapper keys (sources/outlets/collectors/
// collector/enrollmentToken/...) ARE already camelCase because the handlers write them as
// anonymous-object literals. Normalize the DTO items to camelCase here, at the client boundary,
// so the rest of the app (slice/selectors/components) never has to know about the split — this
// mirrors the app's own api/mappers/Mapper.js convention (frontend-side normalization), just
// inline since these DTOs are flat (no nested objects to recurse into).
const toCamelKey = key => key.charAt(0).toLowerCase() + key.slice(1);

const toCamelCaseShallow = obj => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [toCamelKey(key), value])
  );
};

const toCamelCaseArray = arr => (arr ?? []).map(toCamelCaseShallow);

class ColoClient extends ApiClient {
  constructor(url) {
    super(url ? `${url}/colo` : '/colo');
  }

  // ---- Sources (colo/sources) ----

  async getSources() {
    try {
      const data = await this.get('/sources');
      return toCamelCaseArray(data?.sources);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to fetch sources.'));
    }
  }

  async createSource(body) {
    try {
      const source = await this.post('/sources', body);
      return toCamelCaseShallow(source);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to create source.'));
    }
  }

  // IMPORTANT: this is a full-replace PUT — body.collectorId must be sent even when unchanged,
  // or the existing collector assignment is cleared server-side (UpdateSourceConfigCommand).
  async updateSource(id, body) {
    try {
      const source = await this.put(`/sources/${id}`, body);
      return toCamelCaseShallow(source);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to update source.'));
    }
  }

  async testSource(id) {
    try {
      // testSucceeded/detail/testedAt are already camelCase (anonymous-object response).
      return await this.post(`/sources/${id}/test`);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to test source.'));
    }
  }

  async enableSource(id, enabled) {
    try {
      const source = await this.put(`/sources/${id}/enable`, { enabled });
      return toCamelCaseShallow(source);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update source enablement.')
      );
    }
  }

  // GET colo/sources/{id}/foreman-status — live host↔device match diagnostic for a Foreman
  // source. Unlike the flat source/outlet DTOs, this response nests arrays, so the top-level keys
  // AND each array item are normalized to camelCase (toCamelCaseShallow only touches one level).
  async getForemanStatus(id) {
    try {
      const data = await this.get(`/sources/${id}/foreman-status`);
      return {
        ...toCamelCaseShallow(data),
        matched: toCamelCaseArray(data?.Matched ?? data?.matched),
        unmatchedDevices: toCamelCaseArray(
          data?.UnmatchedDevices ?? data?.unmatchedDevices
        ),
        unmatchedHosts: toCamelCaseArray(
          data?.UnmatchedHosts ?? data?.unmatchedHosts
        )
      };
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch Foreman status.')
      );
    }
  }

  // ---- Outlet mappings (colo/outlet-mappings) ----

  async getOutletMappings(sourceId) {
    try {
      const data = await this.get('/outlet-mappings', { sourceId });
      return toCamelCaseArray(data?.outlets);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch outlet mappings.')
      );
    }
  }

  async createOutletMapping(body) {
    try {
      const mapping = await this.post('/outlet-mappings', body);
      return toCamelCaseShallow(mapping);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to create outlet mapping.')
      );
    }
  }

  async closeOutletMapping(id) {
    try {
      const mapping = await this.put(`/outlet-mappings/${id}/close`);
      return toCamelCaseShallow(mapping);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to close outlet mapping.')
      );
    }
  }

  // Body is raw CSV text (backend does not validate content-type); per-row failures come back as
  // { error, rowErrors } on a 400 — re-thrown as-is (with rowErrors attached) so the import dialog
  // can render them per row instead of a single generic message.
  async importOutletMappings(sourceId, csvText) {
    try {
      const data = await this.post('/outlet-mappings/import', csvText, {
        sourceId
      });
      return data;
    } catch (error) {
      const rowErrors = error?.response?.data?.rowErrors;
      const wrapped = new Error(
        extractErrorMessage(error, 'Failed to import outlet mappings.')
      );
      wrapped.rowErrors = rowErrors;
      throw wrapped;
    }
  }

  // ---- Collectors (colo/collectors) ----

  async getCollectors() {
    try {
      const data = await this.get('/collectors');
      return toCamelCaseArray(data?.collectors);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch collectors.')
      );
    }
  }

  // Response is { collector, enrollmentToken } — enrollmentToken is plaintext and shown exactly
  // once by the caller (see CollectorAdminHandler.CreateCollector's doc comment).
  async createCollector(body) {
    try {
      const data = await this.post('/collectors', body);
      return {
        collector: toCamelCaseShallow(data?.collector),
        enrollmentToken: data?.enrollmentToken
      };
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to create collector.')
      );
    }
  }

  async revokeCollector(id) {
    try {
      const collector = await this.put(`/collectors/${id}/revoke`);
      return toCamelCaseShallow(collector);
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to revoke collector.')
      );
    }
  }
}

export default ColoClient;
